import { useEffect, useState } from "react";
import {
    Plus,
    X,
    Edit3,
    Trash2,
    Search,
    ChevronLeft,
    ChevronRight,
    Globe,
    Phone,
    MapPin,
    Info,
    Upload,
    Image as ImageIcon,
} from "lucide-react";
import Swal from "sweetalert2";
import API_BASE_URL from "../config";

export default function WebsiteInfoPage({ setCurrentPage }) {
    const [websites, setWebsites] = useState([]);
    const [filtered, setFiltered] = useState([]);

    const [openCreate, setOpenCreate] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [selectedWebsite, setSelectedWebsite] = useState(null);

    const [websiteName, setWebsiteName] = useState("");
    const [websiteLogo, setWebsiteLogo] = useState("");
    const [websiteSlogan, setWebsiteSlogan] = useState("");
    const [websiteInfo, setWebsiteInfo] = useState("");
    const [websiteAddress, setWebsiteAddress] = useState("");
    const [websiteContact, setWebsiteContact] = useState("");
    const [websiteIpAddress, setWebsiteIpAddress] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [logoFile, setLogoFile] = useState(null);

    const [search, setSearch] = useState("");

    // Pagination
    const [page, setPage] = useState(1);
    const perPage = 8;
    const totalPages = Math.ceil(filtered.length / perPage);
    const paginated = filtered.slice((page - 1) * perPage, page * perPage);

    // ---------------- API FUNCTIONS ---------------- //

    const fetchWebsites = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}api/Website_Details`);
            if (!res.ok) throw new Error("Failed to fetch website details");

            const data = await res.json();

            const mapped = data.map((w) => ({
                id: w.id,
                websiteName: w.websiteName,
                websiteLogo: w.hasLogo ? w.id : null, // Fix: use hasLogo from backend
                websiteSlogan: w.websiteSlogan,
                websiteInfo: w.websiteInfo,
                websiteAddress: w.websiteAddress,
                websiteContact: w.websiteContact,
                websiteIpadrress: w.websiteIpadrress,
                isActive: w.isActive,
            }));

            setWebsites(mapped);
            setFiltered(mapped);
        } catch (error) {
            console.error(error);
            Swal.fire("Error", "Failed to fetch website details", "error");
        }
    };

    const uploadLogo = async (file, websiteId) => {
        if (!file) return null;
        const formData = new FormData();
        formData.append("file", file);

        try {
            const res = await fetch(`${API_BASE_URL}api/Website_Details/upload-logo/${websiteId}`, {
                method: "POST",
                body: formData,
            });
            if (!res.ok) throw new Error("Logo upload failed");
            return await res.json();
        } catch (err) {
            console.error(err);
            Swal.fire("Error", "Failed to upload logo", "error");
            return null;
        }
    };
    // Key fixes in createWebsite and updateWebsite functions:

    const createWebsite = async () => {
        if (!websiteName.trim()) {
            Swal.fire("Oops!", "Website Name is required", "warning");
            return;
        }

        // Send camelCase - ASP.NET Core automatically converts to PascalCase
        const body = {
            websiteName: websiteName.trim(),
            websiteSlogan: websiteSlogan?.trim() || null,
            websiteInfo: websiteInfo?.trim() || null,
            websiteAddress: websiteAddress?.trim() || null,
            websiteContact: websiteContact?.trim() || null,
            websiteIpadrress: websiteIpAddress?.trim() || null,
            isActive: isActive
        };

        console.log("Sending:", body);

        try {
            const res = await fetch(`${API_BASE_URL}api/Website_Details`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(body),
            });

            if (!res.ok) {
                const errorData = await res.json().catch(() => ({ error: "Unknown error" }));
                console.error("Error response:", errorData);
                throw new Error(errorData.error || "Failed to create website");
            }

            const newWebsite = await res.json();
            console.log("Created:", newWebsite);

            // Upload logo if file is selected
            if (logoFile && newWebsite.id) {
                await uploadLogo(logoFile, newWebsite.id);
            }

            setOpenCreate(false);
            clearForm();
            fetchWebsites();
            Swal.fire("Success!", "Website created successfully", "success");
        } catch (err) {
            console.error("Error:", err);
            Swal.fire("Error", err.message || "Failed to create website", "error");
        }
    };

    const updateWebsite = async () => {
        if (!websiteName.trim()) {
            Swal.fire("Oops!", "Website Name is required", "warning");
            return;
        }

        // Upload logo first if there's a new file
        if (logoFile && selectedWebsite.id) {
            await uploadLogo(logoFile, selectedWebsite.id);
        }

        const body = {
            id: selectedWebsite.id,
            websiteName: websiteName.trim(),
            websiteSlogan: websiteSlogan?.trim() || null,
            websiteInfo: websiteInfo?.trim() || null,
            websiteAddress: websiteAddress?.trim() || null,
            websiteContact: websiteContact?.trim() || null,
            websiteIpadrress: websiteIpAddress?.trim() || null,
            isActive: isActive
        };

        try {
            const res = await fetch(
                `${API_BASE_URL}api/Website_Details/${selectedWebsite.id}`,
                {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(body),
                }
            );

            if (!res.ok) {
                const errorData = await res.json().catch(() => ({ error: "Unknown error" }));
                throw new Error(errorData.error || "Failed to update website");
            }

            setOpenEdit(false);
            setSelectedWebsite(null);
            clearForm();
            fetchWebsites();
            Swal.fire("Updated!", "Website updated successfully", "success");
        } catch (err) {
            console.error(err);
            Swal.fire("Error", err.message, "error");
        }
    };
    const deleteWebsite = async (website) => {
        const result = await Swal.fire({
            title: `Are you sure?`,
            text: `You are about to delete ${website.websiteName}`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
        });

        if (result.isConfirmed) {
            try {
                const res = await fetch(
                    `${API_BASE_URL}api/Website_Details/${website.id}`,
                    { method: "DELETE" }
                );
                if (!res.ok) throw new Error("Failed to delete website");
                fetchWebsites();
                Swal.fire("Deleted!", "Website has been deleted.", "success");
            } catch (err) {
                console.error(err);
                Swal.fire("Error", err.message, "error");
            }
        }
    };

    // ---------------- USE EFFECTS ---------------- //

    useEffect(() => {
        fetchWebsites();
    }, []);

    useEffect(() => {
        if (!search.trim()) setFiltered(websites);
        else {
            const term = search.toLowerCase();
            setFiltered(
                websites.filter(
                    (w) =>
                        w.websiteName?.toLowerCase().includes(term) ||
                        w.websiteSlogan?.toLowerCase().includes(term)
                )
            );
        }
        setPage(1);
    }, [search, websites]);

    // ---------------- HELPERS ---------------- //

    const clearForm = () => {
        setWebsiteName("");
        setWebsiteLogo("");
        setWebsiteSlogan("");
        setWebsiteInfo("");
        setWebsiteAddress("");
        setWebsiteContact("");
        setWebsiteIpAddress("");
        setIsActive(true);
        setLogoFile(null);
    };

    const openEditModal = (w) => {
        setSelectedWebsite(w);
        setWebsiteName(w.websiteName);
        setWebsiteLogo(w.websiteLogo);
        setWebsiteSlogan(w.websiteSlogan);
        setWebsiteInfo(w.websiteInfo);
        setWebsiteAddress(w.websiteAddress);
        setWebsiteContact(w.websiteContact);
        setWebsiteIpAddress(w.websiteIpadrress);
        setIsActive(w.isActive);
        setOpenEdit(true);
    };

    // ---------------- RETURN UI ---------------- //
    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8">
            {/* BACK BUTTON */}
            <button
                onClick={() => setCurrentPage("referencess")}
                className="mb-2 flex items-center gap-5 text-indigo-600 hover:text-indigo-800 font-semibold transition-colors group ml-8"
            >
                <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                Back to References
            </button>

            {/* HEADER */}
            <div className="mb-8">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-4xl font-bold text-gray-800 flex items-center gap-3">
                            <Globe className="text-indigo-600" size={36} />
                            Website Information
                        </h1>
                        <p className="text-gray-600 mt-2">Manage your website details and configuration</p>
                    </div>
                    <button
                        onClick={() => setOpenCreate(true)}
                        className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-6 py-3 rounded-xl hover:from-indigo-700 hover:to-indigo-800 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                    >
                        <Plus size={20} /> Add Website Info
                    </button>
                </div>
            </div>

            {/* SEARCH BAR */}
            <div className="mb-6">
                <div className="relative max-w-md">
                    <Search size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by website name or slogan..."
                        className="w-full pl-12 pr-4 py-3 bg-white rounded-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all shadow-sm"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            {/* TABLE CARD */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white">
                                <th className="px-6 py-4 text-left text-sm font-semibold">Logo</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold">Website Name</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold">Slogan</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold">Info</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold">Address</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold">Contact</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold">IP Address</th>
                                <th className="px-6 py-4 text-center text-sm font-semibold">Status</th>
                                <th className="px-6 py-4 text-center text-sm font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {paginated.length > 0 ? (
                                paginated.map((w, index) => (
                                    <tr key={w.id || index} className="hover:bg-indigo-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center justify-center w-16 h-16 bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200">
                                                {w.websiteLogo ? (
                                                    <img
                                                        src={`${API_BASE_URL}api/Website_Details/logo/${w.id}`}
                                                        alt="Logo"
                                                        className="w-full h-full object-contain"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.parentElement.innerHTML = '<div class="text-gray-400 text-xs">No Logo</div>';
                                                        }}
                                                    />
                                                ) : (
                                                    <Globe size={24} className="text-gray-400" />
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="font-semibold text-gray-800">{w.websiteName}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-600 italic">{w.websiteSlogan || "—"}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-600 max-w-xs truncate" title={w.websiteInfo}>
                                                <div className="flex items-start gap-2">
                                                    <Info size={14} className="mt-1 text-gray-400 flex-shrink-0" />
                                                    {w.websiteInfo || "—"}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-600">
                                                <div className="flex items-start gap-2">
                                                    <MapPin size={14} className="mt-1 text-gray-400 flex-shrink-0" />
                                                    {w.websiteAddress || "—"}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-600">
                                                <div className="flex items-center gap-2">
                                                    <Phone size={14} className="text-gray-400" />
                                                    {w.websiteContact || "—"}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-600 font-mono">
                                                {w.websiteIpadrress || "—"}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${w.isActive
                                                ? 'bg-green-100 text-green-700'
                                                : 'bg-red-100 text-red-700'
                                                }`}>
                                                {w.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex justify-center gap-2">
                                                <button
                                                    onClick={() => openEditModal(w)}
                                                    className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md"
                                                    title="Edit"
                                                >
                                                    <Edit3 size={16} />
                                                </button>
                                                <button
                                                    onClick={() => deleteWebsite(w)}
                                                    className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md"
                                                    title="Delete"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="9" className="px-6 py-16 text-center">
                                        <div className="flex flex-col items-center gap-3 text-gray-400">
                                            <Globe size={48} />
                                            <p className="text-lg font-medium">No website details found</p>
                                            <p className="text-sm">Try adjusting your search or add new website info</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* PAGINATION */}
                {paginated.length > 0 && (
                    <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">
                                Showing {(page - 1) * perPage + 1} to {Math.min(page * perPage, filtered.length)} of {filtered.length} websites
                            </span>
                            <div className="flex items-center gap-3">
                                <button
                                    disabled={page <= 1}
                                    onClick={() => setPage(page - 1)}
                                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                                >
                                    <ChevronLeft size={18} />
                                </button>
                                <span className="text-sm font-semibold text-gray-700">
                                    Page {page} of {totalPages || 1}
                                </span>
                                <button
                                    disabled={page >= totalPages}
                                    onClick={() => setPage(page + 1)}
                                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                                >
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* MODAL */}
            {(openCreate || openEdit) && (
                <Modal
                    title={openCreate ? "Add New Website Info" : "Edit Website Info"}
                    close={() => {
                        setOpenCreate(false);
                        setOpenEdit(false);
                        setSelectedWebsite(null);
                        clearForm();
                    }}
                >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="space-y-4">
                            <Input label="Website Name*" value={websiteName} onChange={setWebsiteName} required />
                            <Input label="Website Slogan" value={websiteSlogan} onChange={setWebsiteSlogan} />
                            <TextArea label="Website Info" value={websiteInfo} onChange={setWebsiteInfo} />
                        </div>
                        <div className="space-y-4">
                            <TextArea label="Website Address" value={websiteAddress} onChange={setWebsiteAddress} />
                            <Input label="Contact Number" value={websiteContact} onChange={setWebsiteContact} />
                            <Input label="IP Address" value={websiteIpAddress} onChange={setWebsiteIpAddress} />
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Status
                                </label>
                                <div className="flex items-center gap-4">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            checked={isActive === true}
                                            onChange={() => setIsActive(true)}
                                            className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                                        />
                                        <span className="text-sm text-gray-700">Active</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            checked={isActive === false}
                                            onChange={() => setIsActive(false)}
                                            className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                                        />
                                        <span className="text-sm text-gray-700">Inactive</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div
                            className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl p-6 hover:border-indigo-500 hover:bg-indigo-50 transition cursor-pointer group"
                            onClick={() => document.getElementById("logoInput").click()}
                            onDrop={(e) => {
                                e.preventDefault();
                                if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                                    setLogoFile(e.dataTransfer.files[0]);
                                }
                            }}
                            onDragOver={(e) => e.preventDefault()}
                        >
                            {logoFile ? (
                                <div className="text-center space-y-3">
                                    <img
                                        src={URL.createObjectURL(logoFile)}
                                        alt="Logo Preview"
                                        className="object-contain h-32 w-32 mx-auto rounded-lg"
                                    />
                                    <p className="text-sm text-gray-600">{logoFile.name}</p>
                                </div>
                            ) : selectedWebsite?.websiteLogo ? (
                                <div className="text-center space-y-3">
                                    <img
                                        src={`${API_BASE_URL}api/Website_Details/logo/${selectedWebsite.id}`}
                                        alt="Current Logo"
                                        className="object-contain h-32 w-32 mx-auto rounded-lg"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                    <p className="text-sm text-gray-600">Current Logo</p>
                                </div>
                            ) : (
                                <div className="text-center space-y-3">
                                    <ImageIcon size={48} className="mx-auto text-gray-400 group-hover:text-indigo-500 transition" />
                                    <p className="text-gray-500 font-medium">
                                        Drag & Drop logo here
                                    </p>
                                    <p className="text-sm text-gray-400">
                                        or click to browse
                                    </p>
                                    <p className="text-xs text-gray-400">
                                        PNG, JPG up to 5MB
                                    </p>
                                </div>
                            )}
                            <input
                                type="file"
                                accept="image/*"
                                id="logoInput"
                                className="hidden"
                                onChange={(e) => setLogoFile(e.target.files[0])}
                            />
                        </div>
                    </div>

                    <button
                        onClick={openCreate ? createWebsite : updateWebsite}
                        className="mt-6 w-full bg-gradient-to-r from-indigo-600 to-indigo-700 text-white py-3 rounded-xl hover:from-indigo-700 hover:to-indigo-800 transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
                    >
                        {openCreate ? "Create Website Info" : "Update Website Info"}
                    </button>
                </Modal>
            )}
        </div>
    );
}

// Modal Component
function Modal({ title, close, children }) {
    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-5xl relative max-h-[90vh] overflow-y-auto">
                <button
                    onClick={close}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-2 transition"
                >
                    <X size={24} />
                </button>
                <h2 className="text-3xl font-bold text-gray-800 mb-8">{title}</h2>
                {children}
            </div>
        </div>
    );
}

function Input({ label, value, onChange, type = "text", required = false }) {
    return (
        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
                {label}
            </label>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none"
            />
        </div>
    );
}

function TextArea({ label, value, onChange }) {
    return (
        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
                {label}
            </label>
            <textarea
                value={value}
                onChange={(e) => onChange(e.target.value)}
                rows={3}
                className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none resize-none"
            />
        </div>
    );
}